import { useWorkspaceSettings } from '@easy-expense/data-firestore-client';
import { Category, EntityFilter } from '@easy-expense/data-schema-v2';
import Data from '@easy-expense/frontend-data-layer';
import { getTranslation } from '@easy-expense/intl-client';
import { IconName } from '@easy-expense/ui-shared-components';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import {
  useExpenseCategoryUsageCounts,
  usePaymentMethodUsageCounts,
  useVendorUsageCounts,
} from '@easy-expense/utils-client';
import React from 'react';

import { AutoCompleteValue } from '../Shared/Dropdown/AutoCompleteTypes';
import { SearchDropdown } from '../Shared/Dropdown/SearchDropDown.component';
import { deletedAutoCompleteValues } from '../Shared/deletedAutoCompleteValues';
import { useDeletedInUse } from '../Shared/useDeletedInUse';

export const usePaymentMethodsAutoComplete = (): AutoCompleteValue[] => {
  const paymentMethods = Data.paymentMethods.use();
  const usageCounts = usePaymentMethodUsageCounts();
  const deleted = useDeletedInUse(usageCounts, Data.paymentMethods.getByKey, paymentMethods);

  return React.useMemo(() => {
    return [
      ...paymentMethods.map((method) => {
        return {
          key: method.key,
          value: {
            name: method.value.name,
            image: null,
            iconicIcon: 'card-outline' as IconName,
          },
        };
      }),
      // Deleted methods stay filterable while expenses are still filed against them, otherwise
      // there is no way back to that part of your own history.
      ...deletedAutoCompleteValues(deleted, 'card-outline'),
    ];
  }, [paymentMethods, deleted]);
};

export const ExportExpenseFilters: React.FC<{
  entityFilter: EntityFilter;
  setExportFilters: (filters: EntityFilter) => void;
}> = ({ entityFilter, setExportFilters }) => {
  const allVendors = Data.vendors.use();
  const categories: Category[] = Data.expenseCategories.use();
  const paymentMethods = usePaymentMethodsAutoComplete();
  const deletedVendors = useDeletedInUse(useVendorUsageCounts(), Data.vendors.getByKey, allVendors);
  const deletedCategories = useDeletedInUse(
    useExpenseCategoryUsageCounts(),
    Data.expenseCategories.getByKey,
    categories,
  );
  const { workspaceSettings } = useWorkspaceSettings();

  return (
    <Layout.Column>
      <Layout.Row align>
        <OpenSans.Primary size={22} weight="bold-700">
          {getTranslation('Expense Filters')}
        </OpenSans.Primary>
      </Layout.Row>
      <Spacer.Vertical size={8} />
      <Layout.Row align>
        <SearchDropdown
          header={getTranslation('Vendors')}
          placeholder={getTranslation('All Vendors')}
          selectedKey={entityFilter.vendors?.length ? entityFilter.vendors[0] : null}
          autoCompleteValues={[
            { key: undefined, value: { name: 'All Vendors', iconicIcon: 'storefront-outline' } },
            ...allVendors,
            ...deletedAutoCompleteValues(deletedVendors, 'storefront-outline'),
          ]}
          onSelect={(c) => {
            setExportFilters({ ...entityFilter, vendors: c?.key ? [c.key] : null });
          }}
          error={false}
          clearOnSelect={true}
          iconicIcon="storefront-outline"
        />
        <Spacer.Horizontal />

        <SearchDropdown
          header={getTranslation('Category')}
          placeholder={getTranslation('All Categories')}
          selectedKey={entityFilter.categories?.length ? entityFilter.categories[0] : null}
          autoCompleteValues={[
            { key: undefined, value: { name: 'All Categories', iconicIcon: 'pricetag-outline' } },
            ...categories,
            ...deletedAutoCompleteValues(deletedCategories, 'pricetag-outline'),
          ]}
          onSelect={(c) => {
            setExportFilters({ ...entityFilter, categories: c?.key ? [c.key] : null });
          }}
          error={false}
          iconicIcon="pricetag-outline"
          clearOnSelect={true}
        />
        <Spacer.Horizontal />
        <SearchDropdown
          header={getTranslation('Payment Methods')}
          placeholder={getTranslation('All Methods')}
          selectedKey={entityFilter.paymentMethods?.length ? entityFilter.paymentMethods[0] : null}
          autoCompleteValues={[
            { key: undefined, value: { name: 'All Payment Methods', iconicIcon: 'card-outline' } },
            ...paymentMethods,
          ]}
          onSelect={(c) => {
            setExportFilters({ ...entityFilter, paymentMethods: c?.key ? [c.key] : null });
          }}
          error={false}
          iconicIcon="card-outline"
          clearOnSelect={true}
        />
      </Layout.Row>

      <Spacer.Vertical size={16} />

      {workspaceSettings?.showReimbursementsToggle ? (
        <Layout.Column>
          <Layout.Row>
            <SearchDropdown
              header={getTranslation('Reimbursable')}
              placeholder={getTranslation('Reimbursable')}
              selectedKey={getReimbursedSelectedKey(entityFilter)}
              autoCompleteValues={[
                { key: '0', value: { name: 'All', iconicIcon: 'cash-outline' } },
                {
                  key: '1',
                  value: { name: 'Has Been Reimbursed', iconicIcon: 'checkmark-circle-outline' },
                },
                {
                  key: '2',
                  value: { name: 'Needs Reimbursement', iconicIcon: 'alert-circle-outline' },
                },
                {
                  key: '3',
                  value: { name: 'Not Reimbursable', iconicIcon: 'close-circle-outline' },
                },
              ]}
              onSelect={(c) => {
                if (c?.key === '1') {
                  setExportFilters({
                    ...entityFilter,
                    isReimbursable: true,
                    reimbursableState: 'reimbursed',
                  });
                } else if (c?.key === '2') {
                  setExportFilters({
                    ...entityFilter,
                    isReimbursable: true,
                    reimbursableState: 'notReimbursed',
                  });
                } else if (c?.key === '3') {
                  setExportFilters({
                    ...entityFilter,
                    isReimbursable: false,
                    reimbursableState: null,
                  });
                } else {
                  setExportFilters({
                    ...entityFilter,
                    isReimbursable: null,
                    reimbursableState: null,
                  });
                }
              }}
              error={false}
              iconicIcon="cash-outline"
              clearOnSelect={true}
            />
          </Layout.Row>
          <Spacer.Vertical size={16} />
        </Layout.Column>
      ) : null}
    </Layout.Column>
  );
};

function getReimbursedSelectedKey(entityFilter: EntityFilter): string | null {
  if (entityFilter.isReimbursable === true && entityFilter.reimbursableState === 'reimbursed') {
    return '1';
  } else if (
    entityFilter.isReimbursable === true &&
    entityFilter.reimbursableState === 'notReimbursed'
  ) {
    return '2';
  } else if (entityFilter.isReimbursable === false) {
    return '3';
  }
  return '0';
}
